import React, { useEffect, useState, useCallback } from 'react';
import { X, RotateCcw } from 'lucide-react';

const AccessibilityPersonIcon: React.FC<{ size?: number }> = ({ size = 56 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="#38a9ff"
    stroke="#38a9ff"
    aria-hidden="true"
  >
    <path d="M24,2A22,22,0,1,0,46,24,21.9,21.9,0,0,0,24,2Zm0,8a3,3,0,1,1-3,3A2.9,2.9,0,0,1,24,10ZM35,21H28v4.8l2,9.8A2.1,2.1,0,0,1,28.4,38H28a2.1,2.1,0,0,1-2-1.6L24.2,27h-.4L22,36.4A2.1,2.1,0,0,1,20,38h-.4A2.1,2.1,0,0,1,18,35.6l2-9.8V21H13a2,2,0,0,1,0-4H35a2,2,0,0,1,0,4Z" />
  </svg>
);

type Settings = {
  highContrast: boolean;
  grayscale: boolean;
  largeText: 0 | 1 | 2; // 0 = normal, 1 = 120%, 2 = 150%
  highlightLinks: boolean;
  readableFont: boolean;
};

const DEFAULTS: Settings = {
  highContrast: false,
  grayscale: false,
  largeText: 0,
  highlightLinks: false,
  readableFont: false,
};

const STORAGE_KEY = 'a11y-settings-v1';

const applySettings = (s: Settings) => {
  const root = document.documentElement;
  root.classList.toggle('a11y-high-contrast', s.highContrast);
  root.classList.toggle('a11y-grayscale', s.grayscale);
  root.classList.toggle('a11y-large-text-1', s.largeText === 1);
  root.classList.toggle('a11y-large-text-2', s.largeText === 2);
  root.classList.toggle('a11y-highlight-links', s.highlightLinks);
  root.classList.toggle('a11y-readable-font', s.readableFont);
};

export const AccessibilityWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Settings>(DEFAULTS);

  // Load persisted settings on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = { ...DEFAULTS, ...JSON.parse(raw) } as Settings;
        setSettings(parsed);
        applySettings(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Apply + persist on change
  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* ignore */
    }
  }, [settings]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const update = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = () => setSettings(DEFAULTS);

  return (
    <>
      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="פתח תפריט נגישות"
        aria-expanded={open}
        aria-controls="a11y-panel"
        className="fixed bottom-5 left-5 z-[9998] w-14 h-14 rounded-full flex items-center justify-center drop-shadow-xl hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-yellow-300"
      >
        <AccessibilityPersonIcon size={56} />
      </button>

      {/* Panel */}
      {open && (
        <div
          id="a11y-panel"
          role="dialog"
          aria-modal="false"
          aria-label="הגדרות נגישות"
          dir="rtl"
          className="fixed bottom-24 left-5 z-[9999] w-[320px] max-w-[calc(100vw-2.5rem)] max-h-[80vh] overflow-y-auto bg-white text-black border-2 border-black shadow-2xl rounded-md"
          style={{ fontFamily: 'Arial, system-ui, sans-serif' }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#1e40af] text-white border-b-2 border-black">
            <h2 className="text-lg font-bold m-0">תפריט נגישות</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="סגור תפריט נגישות"
              className="p-1 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-300 rounded"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <ToggleRow
              id="a11y-contrast"
              label="ניגודיות גבוהה"
              description="רקע שחור, טקסט צהוב/לבן"
              checked={settings.highContrast}
              onChange={(v) => update('highContrast', v)}
            />
            <ToggleRow
              id="a11y-grayscale"
              label="גווני אפור"
              description="הסרת צבעים מהאתר"
              checked={settings.grayscale}
              onChange={(v) => update('grayscale', v)}
            />
            <ToggleRow
              id="a11y-links"
              label="הדגשת קישורים"
              description="קו תחתון בולט וצבע מודגש"
              checked={settings.highlightLinks}
              onChange={(v) => update('highlightLinks', v)}
            />
            <ToggleRow
              id="a11y-font"
              label="גופן קריא"
              description="החלפה לגופן Arial / מערכת"
              checked={settings.readableFont}
              onChange={(v) => update('readableFont', v)}
            />

            <fieldset className="border-2 border-black p-3 rounded">
              <legend className="px-1 text-sm font-bold">גודל טקסט</legend>
              <div className="flex gap-2 mt-1" role="radiogroup" aria-label="גודל טקסט">
                {([
                  { v: 0, label: 'רגיל' },
                  { v: 1, label: '120%' },
                  { v: 2, label: '150%' },
                ] as const).map((opt) => {
                  const active = settings.largeText === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => update('largeText', opt.v)}
                      className={`flex-1 px-3 py-2 border-2 border-black font-bold text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                        active ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <button
              type="button"
              onClick={reset}
              className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-white text-black border-2 border-black font-bold hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <RotateCcw size={16} aria-hidden="true" />
              <span>איפוס הגדרות</span>
            </button>

            <a
              href="/accessibility-statement"
              className="block text-center text-sm underline text-[#1e40af] hover:text-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-yellow-400 mt-2"
            >
              הצהרת נגישות
            </a>
          </div>
        </div>
      )}
    </>
  );
};

type ToggleRowProps = {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
};

const ToggleRow: React.FC<ToggleRowProps> = ({ id, label, description, checked, onChange }) => {
  const descId = description ? `${id}-desc` : undefined;
  return (
    <div className="flex items-start justify-between gap-3 border-2 border-black p-3 rounded">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="font-bold text-sm text-black cursor-pointer block">
          {label}
        </label>
        {description && (
          <p id={descId} className="text-xs text-black/70 mt-1 m-0">
            {description}
          </p>
        )}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={descId}
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-7 flex-shrink-0 border-2 border-black rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
          checked ? 'bg-black' : 'bg-white'
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-0.5 w-5 h-5 rounded-full transition-all ${
            checked ? 'right-0.5 bg-white' : 'right-[1.5rem] bg-black'
          }`}
        />
        <span className="sr-only">{checked ? 'מופעל' : 'כבוי'}</span>
      </button>
    </div>
  );
};
